import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Url } from '@wix/da-url';
import BuyButton from '@wix/da-ds/pkg/Buttons/BuyButton';
import BiLink from '@wix/da-bi/pkg/Components/BiLink';
import { BiData, GalleryPurchaseBiEvent, BiContextProvidedValue } from '@wix/da-bi/pkg/events';
import PremiumGalleryIcon from '@wix/da-ds/pkg/Icons/16x16/PremiumGallery';
import LockIcon from '@wix/da-ds/pkg/Icons/24x24/Lock';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import PaywallLayout from '../PaywallLayout';
import { PaywallProps } from '../types';
import { normalizePremiumFolderData } from '../../../../../utils/normalizePremiumFolderData';
import { getDeviationResourceType, isVideoDeviation } from '../../../../_helpers';
import DiscountsPopupTrigger from '../../../../../DiscountsPopupTrigger';
import DiscountLabel from '../../../../../DiscountLabel';
import usePaywallDiscount from './usePaywallDiscount';

import paywallStyles from '../Paywall.scss';

const PremiumFolderPaywall: React.FC<PaywallProps> = ({ deviation, width, height }) => {
  const { t } = useTranslation();

  const { folderId, folderUrl, pointPrice, subproductId } = normalizePremiumFolderData(
    deviation?.premiumFolderData
  );

  const {
    eligibleDiscount,
    allDiscounts,
    formattedDollarPrice,
    formattedDollarPriceAfterDiscount,
  } = usePaywallDiscount(deviation);

  const isVideo = isVideoDeviation(deviation);

  const biData = BiData<GalleryPurchaseBiEvent>({
    evid: 361,
    typeid: getDeviationResourceType(deviation),
    itemid: deviation.deviationId,
    authorid: deviation.author?.useridUuid,
    galleryid: folderId ?? 0,
    points: pointPrice,
    rank: BiContextProvidedValue,
    reason_shown: BiContextProvidedValue,
    section_typeid: BiContextProvidedValue,
    sectionid: BiContextProvidedValue,
    sectionname: BiContextProvidedValue,
    sectionrank: BiContextProvidedValue,
    sectiontype: BiContextProvidedValue,
    sort_type: BiContextProvidedValue,
    widgetname: BiContextProvidedValue,
  });

  return (
    <PaywallLayout
      deviation={deviation}
      iconClassName={paywallStyles['lock-icon']}
      icon={<LockIcon size={IconSize.MEDIUM} />}
      title={t('deviationview.paywall.artstage.premiumFolder.title')}
      text={
        isVideo ? (
          /* prettier-ignore */
          <Trans i18nKey="deviationview.paywall.artstage.premiumFolder.text.video">
            To watch, unlock its
            <BiLink className={paywallStyles['folder-preview-button']} href={folderUrl}>
              Premium Folder
            </BiLink>
            and support the artist!
          </Trans>
        ) : (
          /* prettier-ignore */
          <Trans i18nKey="deviationview.paywall.artstage.premiumFolder.text">
          To view, unlock its
          <BiLink className={paywallStyles['folder-preview-button']} href={folderUrl}>
            Premium Folder
          </BiLink>
          and support the artist!
        </Trans>
        )
      }
      button={
        <BuyButton
          href={subproductId ? Url.checkoutLink(subproductId) : undefined}
          biData={biData}
          icon={eligibleDiscount ? undefined : PremiumGalleryIcon}
          variant="solid"
          size="large"
          rounded
        >
          {eligibleDiscount ? (
            <div className={paywallStyles['discount-button-text']}>
              {t('deviationview.paywall.artstage.premiumFolder.button.discount', {
                postProcess: ['reactPostprocessor', 'featureBrancher'],
                price: (
                  <strong className={paywallStyles['price-after-discount']}>
                    {formattedDollarPriceAfterDiscount}
                  </strong>
                ),
                priceBeforeDiscount: (
                  <s className={paywallStyles['price-before-discount']}>{formattedDollarPrice}</s>
                ),
              })}
              <DiscountLabel discount={eligibleDiscount} reversedTheme />
            </div>
          ) : (
            t('deviationview.paywall.artstage.premiumFolder.button', {
              price: formattedDollarPrice,
            })
          )}
        </BuyButton>
      }
      belowButton={
        <DiscountsPopupTrigger allDiscounts={allDiscounts} eligibleDiscount={eligibleDiscount} />
      }
      width={width}
      height={height}
    />
  );
};
PremiumFolderPaywall.displayName = 'PremiumFolderPaywall';

export default PremiumFolderPaywall;
